import React, { useState, useEffect } from 'react';
import { searchUsers } from '../photoFunctions'; // Adjust path as needed

export const UserSearchModal = ({ 
  isOpen, 
  onClose, 
  onSelectUser, 
  currentUserId,
  excludeUserId = null 
}) => {
  const [searchUsername, setSearchUsername] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleSearch = async () => {
    if (!searchUsername.trim()) return;
    setSearching(true);
    try {
      const users = await searchUsers(searchUsername, currentUserId);
      
      if (users.length === 0) {
        // Replaced native alert with UI feedback state in a real app, 
        // but keeping alert as requested to not break functionality logic
        alert('User not found'); 
        setSearchResults([]);
      } else if (users.length === 1) {
        handleSelect(users[0]);
      } else {
        setSearchResults(users);
      }
    } catch (error) {
      console.error('Search failed:', error);
      alert('Search failed. Please try again.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelect = (user) => {
    if (excludeUserId && (user.id === excludeUserId || String(user.id) === String(excludeUserId))) {
       alert('You cannot view your own profile through search');
       return;
    }
    
    onSelectUser(user);
    setSearchUsername('');
    setSearchResults([]);
    onClose();
  };

  if (!isOpen) return null;

  // --- Theme Constants (Matching your Gold/Brown aesthetic) ---
  const THEME_DARK = '#312310';
  
  // iOS Glass Constants
  const GLASS_MODAL_BG = 'rgba(245, 240, 230, 0.75)'; // Warm tinted glass
  const GLASS_BORDER = 'rgba(255, 255, 255, 0.5)';
  const INPUT_BG = 'rgba(49, 35, 16, 0.08)'; // iOS style filled input (dark tint)
  
  // --- Styles ---
  
  const overlayStyle = {
    position: 'fixed', 
    top: 0, left: 0, width: '100%', height: '100%',
    // Darker, smoother blur for the background overlay
    background: 'rgba(0, 0, 0, 0.35)', 
    backdropFilter: 'blur(8px)', // Blurs the content BEHIND the modal
    zIndex: 5000, 
    display: 'flex',
    alignItems: 'center', 
    justifyContent: 'center', 
    padding: '20px',
    // Smooth fade in could be added here with CSS classes
  };

  const modalStyle = {
    // The "Bubble Glass" Container
    background: GLASS_MODAL_BG,
    backdropFilter: 'blur(40px) saturate(150%)', // Heavy "Material" blur
    WebkitBackdropFilter: 'blur(40px) saturate(150%)',
    
    width: '100%', 
    maxWidth: '400px', // Tighter max-width for mobile feel
    padding: '32px 24px',
    borderRadius: '32px', // Large iOS curvature
    border: `1px solid ${GLASS_BORDER}`,
    boxShadow: '0 20px 50px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    transform: 'translateZ(0)', // Hardware acceleration
  };

  const inputStyle = {
    width: '100%', 
    padding: '14px 16px',
    // iOS Standard: Filled background, no border
    background: INPUT_BG, 
    border: 'none',
    borderRadius: '14px', // Squircle input
    
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: '17px', // Standard iOS body size
    color: THEME_DARK,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'background 0.2s',
    WebkitAppearance: 'none' // Removes default iOS inner shadow
  };

  const resultItemStyle = {
    padding: '12px 16px', 
    cursor: 'pointer', 
    display: 'flex', 
    alignItems: 'center', 
    gap: '14px',
    borderBottom: '1px solid rgba(49,35,16, 0.08)',
    transition: 'background 0.2s'
  };

  // iOS Primary Button (Pill)
  const primaryButtonStyle = {
    width: '100%', 
    padding: '16px', 
    background: THEME_DARK, 
    color: '#e6c57e', // Gold text
    border: 'none', 
    borderRadius: '25px', // Full pill shape
    
    fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: '16px',
    fontWeight: '600',
    letterSpacing: '-0.02em',
    cursor: searching ? 'not-allowed' : 'pointer',
    opacity: searching ? 0.7 : 1,
    marginTop: '8px',
    boxShadow: '0 4px 12px rgba(49, 35, 16, 0.2)'
  };

  // iOS Secondary Button (Text only / Ghost)
  const secondaryButtonStyle = {
    width: '100%', 
    padding: '12px', 
    background: 'transparent', 
    color: THEME_DARK,
    border: 'none', 
    fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: '16px',
    cursor: 'pointer',
    opacity: 0.7
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        
        <h2 style={{
          fontFamily: "'Apple Garamond', Garamond, serif", // Kept your brand font
          fontSize: '28px',
          color: THEME_DARK,
          fontWeight: 500, 
          margin: '0 0 8px 0', 
          textAlign: 'center'
        }}>
          Find Users
        </h2>

        <input
          type="text"
          placeholder="Search by username"
          value={searchUsername}
          onChange={(e) => { setSearchUsername(e.target.value); setSearchResults([]); }}
          onKeyPress={(e) => e.key === 'Enter' && !searching && handleSearch()}
          autoFocus
          style={inputStyle}
          // Add focus style logic if moved to CSS, here we rely on the cursor
        />

        {/* Results Area - Styled like a TableView */}
        {searchResults.length > 0 && (
          <div style={{ 
            maxHeight: '240px', 
            overflowY: 'auto', 
            borderRadius: '14px',
            background: 'rgba(255,255,255,0.4)', // Slightly lighter backing for list
            marginTop: '4px'
          }}>
            {searchResults.map((user, index) => (
              <div key={user.id} onClick={() => handleSelect(user)}
                style={{
                  ...resultItemStyle,
                  // Remove border from last item
                  borderBottom: index === searchResults.length - 1 ? 'none' : resultItemStyle.borderBottom
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(49,35,16,0.05)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {user.avatar ? (
                  <img src={user.avatar} alt={user.username} style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  // Fallback avatar if none exists
                  <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(49,35,16,0.1)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <span style={{fontSize:'20px'}}>👤</span>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontFamily: "-apple-system, sans-serif", fontSize: '16px', fontWeight: '500', color: THEME_DARK }}>
                    {user.username}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <button onClick={handleSearch} disabled={searching} style={primaryButtonStyle}>
          {searching ? 'Searching...' : 'Search'}
        </button>
        
        <button onClick={onClose} style={secondaryButtonStyle}>
          Cancel
        </button>
        
      </div>
    </div>
  );
};